import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  List,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useState } from 'react';
import { useSnackbar } from 'notistack';

import { LineSetupModalGupshup } from './line-setup-modal-gupshup.component';
import { LineSetupModalMeta } from './line-setup-modal-meta.component';

import { ChannelRow } from '@/components/company-details/channel-row.component';
import { Channel, Company, isWhatsAppChannel } from '@/services/hsm-backend';

export interface LineSetupProps {
  company: Company;
  refetch: () => Promise<void>;
}

type ChannelState =
  | 'active' // already has an active HSM line → link only
  | 'gupshup' // Gupshup-capable + no line + has number → Gupshup alta
  | 'meta' // Meta-capable + no line + has number → Meta alta (reads Kori)
  | 'no-number' // WhatsApp channel without a number → cannot onboard a line
  | 'no-provider'; // non-HSM channel → no alta possible

function channelState(channel: Channel): ChannelState {
  if (channel.hsmLineStatus === 'active') {
    return 'active';
  }
  if (!channel.provider) {
    return 'no-provider';
  }
  // A WhatsApp line needs a number to exist: without `whatsappNumber` neither
  // provider can be onboarded (Gupshup can't resolve the app; a Meta channel
  // with no number is not a usable line), so the button is disabled for both.
  if (!channel.whatsappNumber) {
    return 'no-number';
  }
  return channel.provider === 'gupshup' ? 'gupshup' : 'meta';
}

/**
 * "Alta de línea HSM" screen. Lists the company channels (reusing `ChannelRow`)
 * and, for each HSM-capable channel without a line, offers the "Dar de alta
 * línea" button that opens the provider-specific modal (Gupshup 2-step / Meta
 * preview). Active channels show a link; channels without an HSM provider show
 * an info icon.
 */
export const LineSetup = ({ company, refetch }: LineSetupProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [active, setActive] = useState<{
    channel: Channel;
    mode: 'gupshup' | 'meta';
  } | null>(null);

  const handleSuccess = async () => {
    setActive(null);
    enqueueSnackbar('Línea HSM dada de alta', { variant: 'success' });
    await refetch();
  };

  const renderAction = (channel: Channel) => {
    const state = channelState(channel);

    if (state === 'active') {
      return (
        <Tooltip title="Línea HSM activa">
          <IconButton size="small" aria-label="Canal con línea activa">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }

    if (state === 'no-provider') {
      return (
        <Tooltip title="Sin proveedor HSM — no se puede dar de alta una línea desde acá">
          <InfoOutlinedIcon fontSize="small" color="disabled" />
        </Tooltip>
      );
    }

    if (state === 'no-number') {
      return (
        <Tooltip title="El canal no tiene número de WhatsApp">
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled
            >
              Activar HSM
            </Button>
          </span>
        </Tooltip>
      );
    }

    return (
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setActive({ channel, mode: state })}
      >
        Activar HSM
      </Button>
    );
  };

  // Solo canales de WhatsApp: el alta HSM no aplica a Facebook/Instagram/web.
  const whatsappChannels = company.channels.filter(isWhatsAppChannel);

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto', px: 3, py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Alta de línea HSM
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Dar de alta líneas HSM por canal.
      </Typography>

      <Card>
        <CardContent>
          {whatsappChannels.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              La empresa no tiene canales de WhatsApp.
            </Typography>
          ) : (
            <List disablePadding>
              {whatsappChannels.map((channel) => (
                <ChannelRow
                  key={channel.channelId}
                  channel={channel}
                  action={renderAction(channel)}
                />
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {active?.mode === 'gupshup' && (
        <LineSetupModalGupshup
          channel={active.channel}
          onClose={() => setActive(null)}
          onSuccess={() => void handleSuccess()}
        />
      )}
      {active?.mode === 'meta' && (
        <LineSetupModalMeta
          channel={active.channel}
          onClose={() => setActive(null)}
          onSuccess={() => void handleSuccess()}
        />
      )}
    </Box>
  );
};
